import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { skillsApi } from '../utils/api';
import { toast } from 'sonner';
import { 
  Lock, Download, Upload, Edit, Sparkles, 
  ChevronDown, ChevronRight, FileText, Settings as SettingsIcon, Package
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { StructuredContent } from '../components/StructuredContent';
import Sidebar from '../components/Sidebar';
import { useSidebar } from '../context/SidebarContext';

const CATEGORIES = [
  'Writing & Content',
  'Business & Productivity',
  'Developer Tools',
  'Data & Analytics',
  'Documents & Files',
  'Marketing & Sales',
  'Creative & Design',
  'Education & Learning',
  'Communication & Support',
  'Research & Knowledge',
  'Legal & Compliance',
  'Translation & Language',
  'Health & Wellness',
  'Finance & Accounting',
  'Engineering & Technical',
  'Custom & Experimental'
];

function EditSkillPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isCollapsed } = useSidebar();
  const [activeSection, setActiveSection] = useState('metadata');
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [skill, setSkill] = useState(null);
  const [expandedFiles, setExpandedFiles] = useState(new Set());
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    visibility: 'DRAFT'
  });

  const sections = [
    {
      id: 'metadata',
      label: 'Metadata',
      icon: Edit
    },
    {
      id: 'package',
      label: 'Package Info',
      icon: Package
    },
    {
      id: 'contents',
      label: 'Package Contents',
      icon: FileText
    }
  ];

  useEffect(() => {
    loadSkill();
  }, [id]);

  const loadSkill = async () => {
    try {
      setLoading(true);
      const res = await skillsApi.get(id);
      const skillData = res.data;
      
      setSkill(skillData);
      setFormData({
        name: skillData.name || '',
        description: skillData.description || '',
        category: skillData.category || skillData.category_id || 'General',
        visibility: skillData.visibility || 'DRAFT'
      });
    } catch (error) {
      console.error('Failed to load skill', error);
      toast.error('Failed to load skill');
      navigate('/my-skills');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await skillsApi.update(skill.id, formData);
      toast.success('Skill updated successfully!');
      navigate(`/skills/${skill.slug}`);
    } catch (error) {
      console.error('Failed to update skill', error);
      toast.error('Failed to update skill. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async () => {
    try {
      if (!skill.package_zip) {
        toast.error('No package available for download');
        return;
      }
      
      const response = await skillsApi.downloadPackage(skill.id);
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${skill.slug}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Package downloaded');
    } catch (error) {
      console.error('Download failed', error);
      toast.error('Failed to download package');
    }
  };

  const toggleFile = (fileName) => {
    const newExpanded = new Set(expandedFiles);
    if (newExpanded.has(fileName)) {
      newExpanded.delete(fileName);
    } else {
      newExpanded.add(fileName);
    }
    setExpandedFiles(newExpanded);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const getPackageFiles = () => {
    console.log('=== GET PACKAGE FILES DEBUG ===');
    console.log('skill object exists:', !!skill);
    console.log('skill.content exists:', !!skill?.content);
    console.log('skill.content type:', typeof skill?.content);
    console.log('skill.content length:', skill?.content?.length);
    console.log('skill.content preview:', skill?.content?.substring(0, 100));
    console.log('skill keys:', skill ? Object.keys(skill) : 'no skill');
    
    const files = [];
    
    // ALWAYS add main skill file FIRST (priority #1)
    if (skill?.content && skill.content.trim().length > 0) {
      console.log('✅ Adding SKILL.md with content length:', skill.content.length);
      files.push({ 
        name: 'SKILL.md (Main File)', 
        content: skill.content,
        type: 'markdown'
      });
    } else {
      console.error('❌ SKILL.md MISSING - skill.content is:', skill?.content);
    }
    
    // Then add bundled resources
    if (!skill?.bundled_resources) {
      console.log('No bundled_resources, returning files:', files.length);
      return files;
    }
    
    const resources = skill.bundled_resources;
    console.log('bundled_resources exists, keys:', Object.keys(resources));
    
    if (resources.prompt_library) {
      files.push({ 
        name: 'Prompt Library', 
        content: resources.prompt_library.content || resources.prompt_library,
        type: 'structured'
      });
    }
    
    if (resources.quick_reference) {
      files.push({ 
        name: 'Quick Reference', 
        content: resources.quick_reference.content || resources.quick_reference,
        type: 'structured'
      });
    }
    
    if (resources.usage_guide) {
      files.push({ 
        name: 'Usage Guide', 
        content: resources.usage_guide.content || resources.usage_guide,
        type: 'structured'
      });
    }
    
    if (resources.decision_guide) {
      files.push({ 
        name: 'Decision Guide', 
        content: resources.decision_guide.content || resources.decision_guide,
        type: 'structured'
      });
    }
    
    if (resources.resource_index) {
      files.push({ 
        name: 'Resource Index', 
        content: resources.resource_index.content || resources.resource_index,
        type: 'structured'
      });
    }
    
    console.log('Total files:', files.length);
    console.log('File names:', files.map(f => f.name));
    console.log('===============================');
    
    return files;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-500 dark:text-gray-400">Loading skill...</p>
        </div>
      </div>
    );
  }

  if (!skill) return null;

  const packageFiles = getPackageFiles();

  return (
    <div className="flex h-screen overflow-hidden bg-bg-gray dark:bg-dark-bg">
      {/* Main App Sidebar */}
      <Sidebar />
      
      {/* Edit Content Area */}
      <div className={`flex flex-1 transition-all duration-300 ${isCollapsed ? 'ml-16' : 'ml-64'}`}>
        {/* Edit Sidebar */}
        <aside className="w-64 border-r border-gray-200 dark:border-[#2e2e2d]">
          <div className="p-6">
            <h1 className="text-2xl font-bold text-text-dark dark:text-dark-text mb-4">Edit Skill</h1>

            {/* Back to Skill Button */}
            <button
              onClick={() => navigate(`/skills/${id}`)}
              className="w-full flex items-center gap-2 px-3 py-2.5 mb-6 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-dark-bg-tertiary hover:bg-gray-200 dark:hover:bg-dark-bg-hover transition-colors"
            >
              <ChevronRight className="h-4 w-4 rotate-180" />
              Back to Skill
            </button>

            <div className="mb-6">
              <h2 className="text-xs font-semibold text-text-gray dark:text-dark-text-secondary uppercase tracking-wider mb-2 px-3">
                SECTIONS
              </h2>
              
              <nav className="space-y-1">
                {sections.map((section) => {
                  const Icon = section.icon;
                  const isActive = activeSection === section.id;
                  
                  return (
                    <button
                      key={section.id}
                      onClick={() => setActiveSection(section.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all text-sm ${
                        isActive
                          ? 'bg-gray-100 dark:bg-dark-bg-tertiary text-text-dark dark:text-dark-text font-medium'
                          : 'text-text-gray dark:text-dark-text-secondary hover:bg-gray-50 dark:hover:bg-dark-bg-hover/50 hover:text-text-dark dark:hover:text-dark-text'
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                      {section.label}
                    </button>
                  );
                })}
              </nav>
            </div>
          </div>
        </aside>

        {/* Edit Content */}
        <main className="flex-1 h-screen overflow-y-auto bg-muted/30">
          <div className="max-w-4xl mx-auto p-8">
            {/* METADATA SECTION */}
            {activeSection === 'metadata' && (
              <div className="max-w-3xl">
                <div className="mb-8">
                  <h2 className="text-3xl font-bold mb-2">Editable Metadata</h2>
                  <p className="text-gray-600 dark:text-gray-400">
                    Changes are visible immediately to all users
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-8">
                  {/* Display Title */}
                  <div className="bg-white dark:bg-dark-bg-secondary rounded-xl border dark:border-[#2e2e2d] p-6">
                    <label className="block mb-2">
                      <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                        Display Title
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                        What appears on skill cards and page
                      </span>
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-3 border dark:border-[#2e2e2d] rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-dark-bg-tertiary transition-colors"
                      maxLength={100}
                      required
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                      {formData.name.length}/100 characters
                    </p>
                  </div>

                  {/* Marketing Description */}
                  <div className="bg-white dark:bg-dark-bg-secondary rounded-xl border dark:border-[#2e2e2d] p-6">
                    <label className="block mb-2">
                      <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                        Marketing Description
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                        Helps people discover your skill
                      </span>
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="w-full px-4 py-3 border dark:border-[#2e2e2d] rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-dark-bg-tertiary min-h-[120px] transition-colors"
                      maxLength={500}
                      required
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                      {formData.description.length}/500 characters
                    </p>
                  </div>

                  {/* Category */}
                  <div className="bg-white dark:bg-dark-bg-secondary rounded-xl border dark:border-[#2e2e2d] p-6">
                    <label className="block mb-2">
                      <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                        Category
                      </span>
                    </label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="w-full px-4 py-3 border dark:border-[#2e2e2d] rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-dark-bg-tertiary transition-colors"
                    >
                      {CATEGORIES.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>

                  {/* Visibility */}
                  <div className="bg-white dark:bg-dark-bg-secondary rounded-xl border dark:border-[#2e2e2d] p-6">
                    <label className="block mb-2">
                      <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                        Visibility
                      </span>
                    </label>
                    <select
                      value={formData.visibility}
                      onChange={(e) => setFormData({ ...formData, visibility: e.target.value })}
                      className="w-full px-4 py-3 border dark:border-[#2e2e2d] rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-dark-bg-tertiary transition-colors"
                    >
                      <option value="DRAFT">Draft - Only you can see this skill</option>
                      <option value="PRIVATE">Private - Only you can access</option>
                      <option value="UNLISTED">Unlisted - Anyone with the link can view</option>
                      <option value="PUBLIC">Public - Everyone can discover and download</option>
                    </select>
                  </div>

                  {/* Submit */}
                  <div className="flex items-center gap-3 pt-4">
                    <Button
                      type="submit"
                      disabled={saving}
                      className="px-6 py-3 bg-gradient-to-r from-primary to-amber-500 text-white font-semibold rounded-lg hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </div>
                </form>
              </div>
            )}

            {/* PACKAGE INFO SECTION */}
            {activeSection === 'package' && (
              <div className="max-w-3xl">
                <div className="mb-8">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-12 h-12 rounded-xl bg-gray-100 dark:bg-dark-bg-tertiary flex items-center justify-center">
                      <Lock className="w-6 h-6 text-gray-500" />
                    </div>
                    <div>
                      <h2 className="text-3xl font-bold">Package Information</h2>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Read-only • Cannot be edited directly</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white dark:bg-dark-bg-secondary rounded-xl border dark:border-[#2e2e2d] p-8 space-y-6">
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Original Package Name</p>
                      <p className="font-mono font-medium text-lg">{skill.slug}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Package Source</p>
                      <p className="font-medium text-lg">{skill.source || 'Upload'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Files in Package</p>
                      <p className="font-medium text-lg">{packageFiles.length} files</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Last Updated</p>
                      <p className="font-medium text-lg">{formatDate(skill.updated_at)}</p>
                    </div>
                  </div>

                  {skill.package_zip && (
                    <div className="pt-6 border-t dark:border-[#2e2e2d]">
                      <button
                        onClick={handleDownload}
                        className="inline-flex items-center gap-2 px-6 py-3 bg-gray-100 dark:bg-dark-bg-tertiary hover:bg-gray-200 dark:hover:bg-dark-bg-hover rounded-lg font-medium transition-colors"
                      >
                        <Download className="w-5 h-5" />
                        Download Full Package
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* PACKAGE CONTENTS SECTION */}
            {activeSection === 'contents' && (
              <div className="max-w-4xl">
                <div className="mb-8 flex items-center justify-between">
                  <div>
                    <h2 className="text-3xl font-bold mb-2">Package Contents</h2>
                    <p className="text-gray-600 dark:text-gray-400">
                      Read-only view of files in the package
                    </p>
                  </div>
                  {skill.package_zip && (
                    <button
                      onClick={handleDownload}
                      className="px-4 py-2 border dark:border-[#2e2e2d] rounded-lg hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary font-medium flex items-center gap-2 transition-colors"
                    >
                      <Download className="w-4 h-4" />
                      Download Full Package
                    </button>
                  )}
                </div>

                {packageFiles.length === 0 ? (
                  <div className="bg-white dark:bg-dark-bg-secondary rounded-xl border dark:border-[#2e2e2d] p-6">
                    <p className="text-gray-500 dark:text-gray-400">No package contents available</p>
                  </div>
                ) : (
                  <div className="bg-white dark:bg-dark-bg-secondary rounded-xl border dark:border-[#2e2e2d] divide-y dark:divide-[#2e2e2d]">
                    {packageFiles.map((file, i) => (
                      <div key={i}>
                        <button
                          onClick={() => toggleFile(file.name)}
                          className="w-full px-6 py-4 flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary text-left transition-colors"
                        >
                          {expandedFiles.has(file.name) ? (
                            <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                          ) : (
                            <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                          )}
                          <FileText className="w-5 h-5 text-gray-400 flex-shrink-0" />
                          <span className="font-medium text-lg">{file.name}</span>
                        </button>

                        {expandedFiles.has(file.name) && (
                          <div className="px-6 pb-6 bg-gray-50 dark:bg-dark-bg-primary">
                            <div className="p-6 bg-white dark:bg-dark-bg-secondary rounded-lg border dark:border-[#2e2e2d]">
                              {(() => {
                                try {
                                  if (!file || !file.content) {
                                    return <p className="text-gray-500">No content available</p>;
                                  }
                                  
                                  if (file.type === 'markdown') {
                                    return (
                                      <div className="prose prose-sm dark:prose-invert max-w-none">
                                        <pre className="whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300">{file.content}</pre>
                                      </div>
                                    );
                                  }
                                  
                                  if (file.type === 'structured' && Array.isArray(file.content)) {
                                    return <StructuredContent sections={file.content} />;
                                  }
                                  
                                  return (
                                    <div className="prose prose-sm dark:prose-invert max-w-none">
                                      <pre className="whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300">
                                        {typeof file.content === 'string' 
                                          ? file.content 
                                          : JSON.stringify(file.content, null, 2)
                                        }
                                      </pre>
                                    </div>
                                  );
                                } catch (error) {
                                  console.error('Error rendering file content:', error);
                                  return <p className="text-red-500">Error displaying content</p>;
                                }
                              })()}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Update Skill CTA */}
                <div className="mt-8 bg-orange-50 dark:bg-dark-bg-tertiary border border-orange-200 dark:border-[#2e2e2d] rounded-xl p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-orange-100 dark:bg-dark-bg-secondary flex items-center justify-center flex-shrink-0">
                      <Upload className="w-6 h-6 text-orange-600 dark:text-orange-500" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg mb-2">Want to Update the Skill Itself?</h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                        To modify the actual skill contents (prompts, documentation, examples),
                        download the package, edit locally, and upload a new version.
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-500 italic">
                        This feature is coming soon. For now, you can download the package and create a new skill with your updates.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}

export default EditSkillPage;
